import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import peopleHero from "@/assets/people-hero.jpg";
import { CtaBand, PageHero } from "@/components/page-parts";
import { FIRM, PEOPLE } from "@/lib/firm";

export const Route = createFileRoute("/our-people")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(`Our People — Michael Klein & Erin Wilkinson | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Meet the lawyers at Coastside Law: Michael Klein, Legal Practice Director admitted 2003, and Erin Wilkinson, solicitor and experienced South East Queensland conveyancer."),
      },
      { property: "og:title", content: fitTitle(`Our People | ${FIRM.name}`) },
      {
        property: "og:description",
        content: fitDescription("The lawyers behind Coastside Law in Golden Beach, Caloundra."),
      },
      { property: "og:url", content: "https://coastsidelaw.com.au/our-people" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/our-people" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": PEOPLE.map((p) => ({
            "@type": "Person",
            "@id": `https://coastsidelaw.com.au/our-people#${p.slug}`,
            name: p.name,
            jobTitle: p.role,
            image: `https://coastsidelaw.com.au${p.photo}`,
            url: "https://coastsidelaw.com.au/our-people",
            worksFor: { "@id": "https://coastsidelaw.com.au/#legalservice" },
            knowsAbout: p.areas,
            sameAs: [p.linkedin],
          })),
        }),
      },
    ],
  }),
  component: PeoplePage,
});

function PeoplePage() {
  return (
    <>
      <PageHero
        image={{ src: peopleHero, alt: "Jetty over calm bay water on a sunny day, Moreton Bay" }}
        eyebrow="Our people"
        title="Two lawyers, two offices — Golden Beach and Scarborough"
        lead="Erin works from Golden Beach on the Sunshine Coast, Michael from Scarborough on the Redcliffe Peninsula. Whichever office you start with, you will know who is handling your matter and you will be able to reach them. That is the whole point of a small local firm."
      />

      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-8 lg:grid-cols-2">
          {PEOPLE.map((p) => (
            <article key={p.slug} className="rounded-sm border border-border bg-card p-6 sm:p-8">
              <div className="flex flex-col gap-5">
                <img
                  src={p.photo}
                  alt={`${p.name}, ${p.role} at Coastside Law`}
                  className="aspect-[4/5] w-full rounded-sm border border-border object-cover object-top shadow-md"
                />
                <div>
                  <h2 className="font-serif text-3xl text-foreground">{p.name}</h2>
                  <p className="mt-2 text-sm font-semibold text-accent-foreground">
                    {p.role} · Admitted {p.admitted}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">Based in {p.based}</p>
                  <a
                    href={p.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm font-semibold text-accent-foreground underline underline-offset-4"
                  >
                    {p.name.split(" ")[0]} on LinkedIn
                  </a>
                </div>
              </div>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">{p.bio}</p>

              {p.slug === "michael-klein" && (
                <Link
                  to="/michael-klein"
                  className="mt-4 inline-block text-sm font-semibold text-accent-foreground underline underline-offset-4"
                >
                  Full profile for Michael Klein
                </Link>
              )}


              <div className="mt-7 grid gap-6 border-t border-border pt-6 sm:grid-cols-2">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground">
                    Admissions
                  </h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                    {p.courts.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground">
                    Areas of practice
                  </h3>
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {p.areas.map((a) => (
                      <li
                        key={a}
                        className="rounded-sm bg-secondary px-2.5 py-1 text-xs text-secondary-foreground"
                      >
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">About the firm</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {FIRM.name} is a Queensland law practice with offices at Golden Beach, Caloundra and
            Scarborough on the Redcliffe Peninsula. We are {FIRM.membership.toLowerCase()}. We act for
            buyers, sellers, families and executors across the Sunshine Coast, Moreton Bay and the
            Wide Bay, and we work on fixed fees so you always know what the legal work costs before it
            starts.
          </p>
        </div>
      </section>

      <CtaBand title="Want to talk it through with one of us?" />
      <div className="h-20" />
    </>
  );
}
